import React, { useCallback, useEffect, useState } from "react";
import {
    Layers,
    Database,
    CheckCircle2,
    AlertTriangle,
    RefreshCw,
    ExternalLink,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getPlatformCoverage } from "@/lib/api";

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 86400) return `hoy`;
        if (diff < 86400 * 2) return `ayer`;
        if (diff < 86400 * 30)
            return `hace ${Math.round(diff / 86400)} días`;
        return new Date(iso).toLocaleDateString("es-ES", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function pctColor(p) {
    if (p >= 90) return "text-emerald-700 bg-emerald-50 border-emerald-200";
    if (p >= 50) return "text-amber-700 bg-amber-50 border-amber-200";
    return "text-rose-700 bg-rose-50 border-rose-200";
}

function KpiCard({ label, value, hint, icon: Icon, accent }) {
    return (
        <div
            className={`rounded-2xl p-5 border-2 ${accent} bg-white shadow-pop`}
        >
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
                    {label}
                </div>
                <Icon size={18} className="opacity-50 shrink-0" />
            </div>
            <div className="font-display text-4xl font-black tracking-tight leading-none">
                {value}
            </div>
            {hint && (
                <div className="text-xs font-medium opacity-60 mt-2">{hint}</div>
            )}
        </div>
    );
}

function Bar({ pct, color }) {
    const safe = Math.max(2, Math.min(100, pct || 0));
    return (
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden w-32">
            <div
                className={`${color} h-full rounded-full transition-all`}
                style={{ width: `${safe}%` }}
            />
        </div>
    );
}

export default function Cobertura() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const d = await getPlatformCoverage();
            setData(d);
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "No se pudo cargar la cobertura. Verifica que seas super-admin.",
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const summary = data?.summary || {};
    const rows = data?.rows || [];

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-10 space-y-8">
                <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
                            Análisis interno · solo super-admin
                        </div>
                        <h1
                            className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none"
                            data-testid="cobertura-title"
                        >
                            Cobertura de plataformas
                        </h1>
                        <p className="text-slate-600 mt-3 max-w-2xl">
                            % de licitaciones importadas en las que los pliegos
                            se descargaron automáticamente, agrupadas por
                            plataforma origen. Usa estos datos para priorizar
                            en qué scrapers invertir tiempo.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        className="rounded-xl border-slate-300 shrink-0"
                        onClick={load}
                        disabled={loading}
                        data-testid="cobertura-refresh-btn"
                    >
                        <RefreshCw
                            size={14}
                            className={`mr-2 ${loading ? "animate-spin" : ""}`}
                        />
                        Refrescar
                    </Button>
                </header>

                {error && (
                    <div
                        className="rounded-2xl border border-rose-200 bg-rose-50/50 p-5 text-sm text-rose-900"
                        data-testid="cobertura-error"
                    >
                        {error}
                    </div>
                )}

                {loading && !data && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {[1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} className="h-28 w-full" />
                            ))}
                        </div>
                        <Skeleton className="h-72 w-full" />
                    </div>
                )}

                {!loading && data && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KpiCard
                                label="Plataformas"
                                value={summary.platforms || 0}
                                hint="Hosts únicos detectados"
                                icon={Layers}
                                accent="border-slate-200"
                            />
                            <KpiCard
                                label="Licitaciones"
                                value={(summary.total_tenders || 0).toLocaleString(
                                    "es-ES",
                                )}
                                hint="Total importadas (todas)"
                                icon={Database}
                                accent="border-slate-200"
                            />
                            <KpiCard
                                label="Con pliegos"
                                value={(summary.with_docs || 0).toLocaleString(
                                    "es-ES",
                                )}
                                hint={`${summary.coverage_pct ?? 0}% cobertura global`}
                                icon={CheckCircle2}
                                accent="border-emerald-200"
                            />
                            <KpiCard
                                label="Sin pliegos"
                                value={(
                                    (summary.total_tenders || 0) -
                                    (summary.with_docs || 0)
                                ).toLocaleString("es-ES")}
                                hint="Requieren subida manual"
                                icon={AlertTriangle}
                                accent="border-amber-200"
                            />
                        </div>

                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="font-display text-xl font-semibold">
                                    Desglose por plataforma
                                </h2>
                                <span className="text-xs text-slate-500">
                                    Ordenado por volumen
                                </span>
                            </div>
                            {rows.length === 0 ? (
                                <div className="py-16 text-center text-sm text-slate-400">
                                    Aún no hay licitaciones importadas para
                                    medir cobertura.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                                            <tr>
                                                <th className="text-left px-5 py-3">
                                                    Plataforma
                                                </th>
                                                <th className="text-right px-5 py-3">
                                                    Total
                                                </th>
                                                <th className="text-right px-5 py-3">
                                                    Con pliegos
                                                </th>
                                                <th className="text-left px-5 py-3">
                                                    Cobertura
                                                </th>
                                                <th className="text-right px-5 py-3">
                                                    Analizadas IA
                                                </th>
                                                <th className="text-right px-5 py-3">
                                                    Última
                                                </th>
                                                <th className="text-right px-5 py-3">
                                                    Empresas
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {rows.map((r) => {
                                                const pct = r.coverage_pct ?? 0;
                                                const isHigh = pct >= 90;
                                                const isLow = pct < 50;
                                                const barColor = isHigh
                                                    ? "bg-emerald-500"
                                                    : isLow
                                                      ? "bg-rose-500"
                                                      : "bg-amber-500";
                                                return (
                                                    <tr
                                                        key={r.host}
                                                        className="hover:bg-slate-50/50 transition-colors"
                                                        data-testid={`cobertura-row-${r.host}`}
                                                    >
                                                        <td className="px-5 py-3.5 font-medium text-slate-900 max-w-xs truncate">
                                                            <a
                                                                href={
                                                                    r.host ===
                                                                    "(sin enlace)"
                                                                        ? undefined
                                                                        : `https://${r.host}`
                                                                }
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className={`inline-flex items-center gap-1.5 ${
                                                                    r.host ===
                                                                    "(sin enlace)"
                                                                        ? "text-slate-400 cursor-default"
                                                                        : "hover:text-[#0055ff]"
                                                                }`}
                                                            >
                                                                {r.host}
                                                                {r.host !==
                                                                    "(sin enlace)" && (
                                                                    <ExternalLink
                                                                        size={11}
                                                                        className="opacity-50"
                                                                    />
                                                                )}
                                                            </a>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right font-mono font-semibold text-slate-900">
                                                            {r.total}
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right font-mono text-slate-700">
                                                            {r.with_docs}
                                                        </td>
                                                        <td className="px-5 py-3.5">
                                                            <div className="flex items-center gap-3">
                                                                <span
                                                                    className={`text-xs font-bold px-2 py-0.5 rounded-full border ${pctColor(
                                                                        pct,
                                                                    )} font-mono shrink-0 min-w-[60px] text-center`}
                                                                >
                                                                    {pct}%
                                                                </span>
                                                                <Bar
                                                                    pct={pct}
                                                                    color={barColor}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right font-mono text-slate-600 text-xs">
                                                            {r.analyzed}{" "}
                                                            <span className="text-slate-400">
                                                                ({r.analyzed_pct}
                                                                %)
                                                            </span>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right text-xs text-slate-500">
                                                            {fmtRel(r.last_import)}
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right font-mono text-slate-700 text-xs">
                                                            {r.companies}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5 text-xs text-slate-600 leading-relaxed">
                            <strong className="text-slate-800">
                                Cómo leer estos datos:
                            </strong>{" "}
                            “Cobertura” = % de licitaciones cuya descarga
                            automática de pliegos funcionó. &lt;50% (rojo)
                            indica que vale la pena añadir parser específico
                            para esa plataforma. ≥90% (verde) significa que el
                            scraper actual la cubre bien.
                            <em className="block mt-1.5 not-italic text-slate-500">
                                “(sin enlace)” agrupa tenders creados a mano
                                (drag-drop de PDFs) sin origen PLCSP.
                            </em>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
